import { NonPaginatedResponse } from '../types/NonPaginatedResponse';

type UnwrapResult<T, Key extends string> = Omit<
  NonPaginatedResponse<T>,
  'data'
> &
  Record<Key, T | undefined>;

type WrappedRefetchResult<TResult, T, Key extends string> = Omit<
  TResult,
  'data'
> &
  Omit<NonPaginatedResponse<T>, 'data'> &
  Record<Key, T | undefined>;

export function unwrapNonPaginatedResponseDataAs<T, Key extends string>(
  response: NonPaginatedResponse<T> | undefined,
  as: Key,
): UnwrapResult<T, Key> | undefined;

export function unwrapNonPaginatedResponseDataAs<
  T,
  Key extends string,
  TRefetchResult extends { data: NonPaginatedResponse<T> | undefined },
>(
  response: NonPaginatedResponse<T> | undefined,
  refetch: (...args: any[]) => Promise<TRefetchResult>,
  as: Key,
): UnwrapResult<T, Key> & {
  refetch: (
    ...args: any[]
  ) => Promise<WrappedRefetchResult<TRefetchResult, T, Key>>;
};

export function unwrapNonPaginatedResponseDataAs<T, Key extends string>(
  response: NonPaginatedResponse<T> | undefined,
  refetchOrAs: ((...args: any[]) => Promise<any>) | Key,
  asKey?: Key,
): any {
  if (typeof refetchOrAs === 'string') {
    if (!response) return undefined;
    const { data, ...rest } = response;
    return { ...rest, [refetchOrAs]: data };
  }

  const refetch = refetchOrAs;
  const as = asKey!;

  const wrappedRefetch = async (...args: any[]) => {
    const result = await refetch(...args);
    const { data, ...queryRest } = result;
    const { data: entity, ...responseRest } = data ?? {};
    return { ...queryRest, ...responseRest, [as]: entity };
  };

  const { data, ...rest } = response ?? {};
  return {
    ...rest,
    [as]: data,
    refetch: wrappedRefetch,
  };
}
